/**
 * Generic KPI Card Component
 * A flexible container that can display various chart visualizations
 */

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../../Tw';
import { getBestContrastColor } from '../../../common/color.helper';
import { exportData } from '../../../common/exportUtils';
import GenericMetricChart, {
  ChartDataItem,
} from '../../../components/charts/GenericMetricChart';
import * as Icon from '../../../components/icons';
import LoadingIndicator from '../../../components/LoadingIndicator';
import { Stat } from '../../../components/stats/Stat';
import { StatListItem } from '../../../components/stats/StatListItem';
import { ChartType, WarehouseKPI } from '../kpiModel';
import { getKpiStatusColor } from '../utilityFunctions';
import KpiCardMenu, { MenuOption } from './KpiCardMenu';
import RelatedKpiView from './RelatedKpiView';

interface KpiGenericCardProps {
  className?: string;
  kpi?: WarehouseKPI;

  // Basic KPI info
  title: string;
  subtitle?: string;
  category?: string;
  value?: number | string;
  unit?: string;
  status?: 'critical' | 'warning' | 'good' | 'exceptional';
  trend?: 'up' | 'down' | 'stable';
  isLoading?: boolean;

  // Chart data and configuration
  chartData?: ChartDataItem[];
  defaultChartType?: ChartType;
  forcedChartType?: ChartType;
  allowedChartTypes?: ChartType[];
  showChartTypeSelector?: boolean;
  containerHeight?: number;

  // Center pie configuration
  showCenterPie?: boolean;
  centerPieProps?: any;
  centerPieStat?: React.ReactNode;
  totalValue?: number;
  totalFormattedValue?: string;

  // Legacy trend data (for backward compatibility)
  trendData?: number[];

  // Additional features
  relatedKpis?: WarehouseKPI[];
  variableValues?: Record<string, any>;

  // Event handlers
  onVariableChange?: (name: string, value: any) => void;
  onNavigateToRelated?: (kpiId: string) => void;
  onShowVisualization?: (kpiId: string, visualizationType?: string) => void;
  onChartTypeChange?: (chartType: ChartType) => void;

  // Feature toggles
  enableFeatureToggles?: boolean;
  showRelated?: boolean;
  showTrend?: boolean;
  showActions?: boolean;
  enableExport?: boolean;
}

/**
 * Generic KPI Card component that can display any type of chart visualization
 */
export const KpiGenericCard: React.FC<KpiGenericCardProps> = ({
  kpi,
  title,
  subtitle,
  category,
  value,
  unit,
  status = 'good',
  trend,
  className,
  isLoading = false,
  chartData,
  defaultChartType = 'pie',
  forcedChartType,
  allowedChartTypes,
  showChartTypeSelector = true,
  containerHeight = 250,
  showCenterPie = false,
  centerPieProps,
  centerPieStat,
  totalValue,
  totalFormattedValue,
  trendData,
  relatedKpis = [],
  variableValues = {},
  onVariableChange,
  onNavigateToRelated,
  onShowVisualization,
  onChartTypeChange,
  enableFeatureToggles = false,
  showRelated = false,
  showTrend = false,
  showActions = true,
  enableExport = true,
}) => {
  const { t } = useTranslation('app');

  const [showRelatedKpiDetails, setShowRelatedKpiDetails] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Feature toggles state
  const [showChart, setShowChart] = useState(true);
  const [showBenchmark, setShowBenchmark] = useState(true);

  // Get background and text colors based on status
  const bgColor = getKpiStatusColor(status);
  const textColor = getBestContrastColor(status);

  // Format the trend for display
  const trendDisplay = () => {
    if (!trend) return null;

    const trendIcons = {
      up: <Icon.ArrowTop className="text-trend-up h-4 w-4 fill-current" />,
      down: (
        <Icon.ArrowBottom className="text-trend-down h-4 w-4 fill-current" />
      ),
      stable: (
        <Icon.ArrowsTwoWay className="text-trend-stable h-4 w-4 fill-current" />
      ),
    };

    return trendIcons[trend];
  };

  // Export functionality
  // Export KPI data as CSV
  const exportAsCSV = () => {
    if (!chartData || chartData.length === 0) return;

    // Create CSV header
    const headers = [kpi?.legend || title || 'Time Period'];

    // Create CSV rows
    const rows = chartData.map((item, index) => {
      return [item.title || `Period ${index + 1}`, item.value];
    });

    // Use exportUtils
    exportData('csv', {
      fileName: `WareBee-${kpi?.id || title.replace(/\s+/g, '-')}-data-${new Date().toISOString().split('T')[0]}`,
      title: title,
      data: rows,
      headers: headers,
      trackingLabel: 'KPI-CSV-Export',
      onComplete: () => console.log('CSV export completed'),
      onError: error => console.error('CSV export failed:', error),
    });
  };

  // Export KPI card as PNG
  const exportAsPNG = async () => {
    if (!cardRef.current) return;
    setIsExporting(true);
    try {
      await exportData('png', {
        fileName: `WareBee-${kpi?.id || title.replace(/\s+/g, '-')}-card-${new Date().toISOString().split('T')[0]}`,
        title: title,
        element: cardRef.current,
        trackingLabel: 'KPI-PNG-Export',
        onComplete: () => {
          setIsExporting(false);
          console.log('PNG export completed');
        },
        onError: error => {
          setIsExporting(false);
          console.error('PNG export failed:', error);
        },
      });
    } catch (error) {
      setIsExporting(false);
      console.error('PNG export failed:', error);
    }
  };

  // Menu options
  const menuOptions: MenuOption[] = [
    ...(enableExport
      ? [
          {
            id: 'export-png',
            label: t`Export as PNG`,
            icon: <Icon.CloudDownload className="h-4 w-4" />,
            onClick: () => exportAsPNG(),
            disabled: isExporting,
          },
          {
            id: 'export-csv',
            label: t`Export as CSV`,
            icon: <Icon.CloudDownloadCsv className="h-4 w-4" />,
            onClick: () => exportAsCSV(),
            disabled: isExporting,
          },
        ]
      : []),
    // ...(enableFeatureToggles
    //   ? [
    //       {
    //         id: 'feature-toggles',
    //         label: t`Feature Toggles`,
    //         icon: <Icon.ViewGraph className="h-4 w-4" />,
    //         onClick: () => {},
    //       },
    //     ]
    //   : []),
    ...(onShowVisualization
      ? [
          {
            id: 'visualize',
            label: t`Show Visualization`,
            icon: <Icon.ViewGraph className="h-4 w-4" />,
            onClick: () =>
              onShowVisualization?.(kpi?.id || title, defaultChartType),
          },
        ]
      : []),
  ];

  // Feature toggle configuration
  // const featureToggles: KpiFeatureToggleConfig[] = [
  //   {
  //     key: 'showChart',
  //     label: t`Show Chart`,
  //     value: showChart,
  //     setValue: setShowChart,
  //   },
  //   {
  //     key: 'showBenchmark',
  //     label: t`Show Benchmarks`,
  //     value: showBenchmark,
  //     setValue: setShowBenchmark,
  //   },
  // ];

  return (
    <div data-component="kpi-generic-card" ref={cardRef} className={className}>
      <Stat
        titleElement={
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-1 items-center">
              <div>
                <div className="font-medium">{kpi?.title}</div>
                {/* {subtitle && (
                  <div className="text-xs opacity-70">{subtitle}</div>
                )} */}
              </div>
            </div>
            <div className="flex items-center">
              {trend && <div className="mr-2 text-2xl">{trendDisplay()}</div>}
              {showActions && menuOptions.length > 0 && (
                <KpiCardMenu options={menuOptions} />
              )}
            </div>
          </div>
        }
        titleAlt={`${kpi?.legend || title} - (${category}) \n ${subtitle}`}
        className="!bg-app-panel-stat"
        value={value}
        unitOfMeasure={unit}
      >
        {isLoading ? (
          <LoadingIndicator selfCenter message={t`Loading Chart...`} />
        ) : (
          <>
            {/* Main Chart */}
            {showChart && chartData && chartData.length > 0 && (
              <div
                data-component="kpi-generic-card-chart"
                className="mt-6 h-full flex-1"
              >
                <GenericMetricChart
                  data={chartData}
                  metricDescriptor={kpi}
                  defaultChartType={defaultChartType}
                  forcedChartType={forcedChartType}
                  // showChartTypeSelector={showChartTypeSelector}
                  containerHeight={containerHeight}
                  showCenterPie={showCenterPie}
                  centerPieProps={centerPieProps}
                  centerPieStat={centerPieStat}
                  totalValue={totalValue}
                  totalFormattedValue={totalFormattedValue}
                  onChartTypeChange={onChartTypeChange}
                />
              </div>
            )}
            {/* Legacy trend data support */}
            {showChart && !chartData && trendData && trendData.length > 0 && (
              <div className="text-menu-text mt-2 text-center text-sm opacity-70">
                {t`Legacy trend data - consider migrating to chartData`}
              </div>
            )}
            {/* Trend statistics */}
            {showTrend && trendData && trendData.length > 1 && (
              <div className="mt-2">
                <StatListItem
                  legendType="line"
                  legendColor={TwTheme.extend.colors.trend.down}
                  title={t`Trend Average`}
                  value={
                    trendData.reduce((a, b) => a + b, 0) / trendData.length
                  }
                  unitOfMeasure={unit}
                />
                <StatListItem
                  title={t`% Change`}
                  value={
                    trendData.length > 1
                      ? `${
                          ((trendData[trendData.length - 1] - trendData[0]) /
                            trendData[0]) *
                          100
                        }%`
                      : '—'
                  }
                  unitOfMeasure="%"
                />
              </div>
            )}
            {/* Related KPIs */}
            {showRelated && relatedKpis && relatedKpis.length > 0 && kpi && (
              <div className="mt-4">
                <RelatedKpiView
                  primaryKpi={kpi}
                  relatedKpis={relatedKpis}
                  onNavigateToKpi={onNavigateToRelated}
                />
              </div>
            )}
          </>
        )}
      </Stat>
    </div>
  );
};

export default KpiGenericCard;
